import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from '../ui/Button';
import { useNavT } from '../../nav/useNavT';

/**
 * Bulk-apply one target's whole schedule to other targets (Scheduling Pro). Used by
 * both schedulers — pass the candidate destinations (already excluding the source).
 * REPLACE semantics: each destination's existing schedule is cleared first, so the
 * dialog spells that out before applying.
 *
 * Props:
 *   visible, onHide
 *   title, sourceLabel, replaceWarning  — display strings
 *   targets: [{ label, value, type }]   — selectable destinations
 *   onApply: (dests:[{type,id}]) => Promise   — performs the copy
 *   loading
 */
export default function CopyScheduleDialog({
  visible, onHide, title = 'Copy schedule', sourceLabel = '',
  replaceWarning = 'This replaces the existing schedule on each selected target.',
  targets = [], onApply, loading = false,
}) {
  const [selected, setSelected] = useState([]);
  const nt = useNavT(); // labels only (new navigation); the original English otherwise

  const apply = async () => {
    if (!selected.length) return;
    const byValue = Object.fromEntries(targets.map((t) => [t.value, t]));
    await onApply(selected.map((v) => ({ type: byValue[v].type, id: v })));
    setSelected([]);
    onHide();
  };

  return (
    <Dialog visible={visible} onHide={() => { setSelected([]); onHide(); }}
      header={title} style={{ width: 'min(95vw, 460px)' }}>
      <div className="d-flex flex-column gap-3 pt-2">
        {sourceLabel && (
          <small style={{ color: 'var(--pp-text-secondary)' }}>
            <i className="pi pi-copy me-1" />{sourceLabel}
          </small>
        )}
        <MultiSelect
          value={selected}
          options={targets}
          onChange={(e) => setSelected(e.value)}
          placeholder={nt('nav6.sched.select_targets', 'Select targets…')}
          display="chip"
          filter
          className="w-100"
          emptyMessage={nt('nav6.sched.no_other_targets', 'No other targets')}
        />
        <div className="p-2" style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid var(--pp-warning)', borderRadius: 'var(--pp-radius-md)' }}>
          <small style={{ color: 'var(--pp-text-primary)' }}>
            <i className="pi pi-exclamation-triangle me-2" style={{ color: 'var(--pp-warning)' }} />
            {replaceWarning}
          </small>
        </div>
        <div className="d-flex justify-content-end gap-2 pt-2" style={{ borderTop: '1px solid var(--pp-border)' }}>
          <Button label={nt('nav6.sched.cancel', 'Cancel')} text onClick={() => { setSelected([]); onHide(); }} />
          <Button label={nt('nav6.sched.copy', 'Copy')} icon="pi pi-check" loading={loading} disabled={!selected.length} onClick={apply} />
        </div>
      </div>
    </Dialog>
  );
}
